import {
  APP_CAREPROVIDER,
  APP_CLINIC,
  NOTIFICATION_PERMISSION_DEFAULT,
} from "@recare/core/consts";
import {
  activateBrowserNotification,
  activatePWA,
} from "@recare/core/model/utils/featureFlags";
import { useEnvContext } from "context/EnvContext";
import { useBrowserContext } from "dsl/atoms/BrowserProvider";
import { ReactNode, createContext, useContext } from "react";
import { useAskNotificationPermission } from "./utils";

export const useAllowBrowserNotifications = (): boolean => {
  const { app } = useEnvContext();

  if (!activateBrowserNotification) {
    return false;
  }

  if (!([APP_CAREPROVIDER, APP_CLINIC] as number[]).includes(app)) {
    return false;
  }

  return true;
};

export const useAllowPWA = (): boolean => {
  const browserContext = useBrowserContext();
  const allowBrowserNotifications = useAllowBrowserNotifications();

  if (!activatePWA || !allowBrowserNotifications) {
    return false;
  }

  const isMobileDevice =
    browserContext.browser?.getPlatformType() === "mobile" &&
    ["ios", "android"].includes(browserContext.browser?.getOSName(true));

  return isMobileDevice;
};

export const useAllowDesktopNotifications = (): boolean => {
  const allowPWA = useAllowPWA();
  const allowBrowserNotifications = useAllowBrowserNotifications();

  return allowBrowserNotifications && !allowPWA;
};

export const NOTIFICATION_PERMISSION_STATUS_UNSUPPORTED = "unsupported";

export type NotificationPermissionState =
  | PermissionStatus["state"]
  | NotificationPermission
  | typeof NOTIFICATION_PERMISSION_STATUS_UNSUPPORTED;

export type NotificationPermissionType = {
  error: string | null;
  isLoading: boolean;
  state: NotificationPermissionState;
};

export type NotificationPermissionContextType = {
  askPermission: () => Promise<void>;
  notificationPermissionState: NotificationPermissionType;
  pushSubscription: PushSubscriptionJSON | null;
  resubscribeUser: () => Promise<void>;
};

export const defaultNotificationPermission: NotificationPermissionType = {
  state: NOTIFICATION_PERMISSION_DEFAULT as NotificationPermissionState,
  isLoading: false,
  error: null,
};

export const NotificationPermissionContext =
  createContext<NotificationPermissionContextType>({
    resubscribeUser: async () => {},
    askPermission: async () => {},
    notificationPermissionState: defaultNotificationPermission,
    pushSubscription: null,
  });

export const useNotificationPermissionContext = () =>
  useContext(NotificationPermissionContext);

export const NotificationPermissionContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const context = useAskNotificationPermission();

  return (
    <NotificationPermissionContext.Provider value={context}>
      {children}
    </NotificationPermissionContext.Provider>
  );
};
